import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@core/services/api/apiRequest";
import type { PendingInvitation } from "../invitation.types";

export type UseFetchPendingInvitationsArgs = {
  /** Current user (query disabled when null) */
  user: { id: number } | null;
  /** Whether the query is enabled */
  isEnabled?: boolean;
};

/**
 * Hook to fetch pending invitations for the current user.
 *
 * Returns invitations that:
 * - Were sent to the user's email
 * - Have not been claimed yet
 * - Are still valid (not expired, active)
 * - User doesn't already hold the invited role
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useFetchPendingInvitations({ user });
 * const invitations = data ?? [];
 * ```
 */
export function useFetchPendingInvitations({
  user,
  isEnabled = true,
}: UseFetchPendingInvitationsArgs) {
  return useQuery<PendingInvitation[]>({
    queryKey: ["pendingInvitations"],
    queryFn: async () => {
      return apiRequest("/invitations/pending/", "GET");
    },
    enabled: !!user && isEnabled,
  });
}
